import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { myLanes } from '../lib/support'
import type { Me } from '../lib/me'
import { can, canEverywhere, canGallery } from '../lib/me'
import { canReachShopAdmin, loadStockRooms } from '../lib/shop'
import type { StoreOption } from '../components/admin/StoreRow'
import { supabase } from '../lib/supabase'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { Dialog } from '../components/Dialog'
import { PeopleTable, STATE_LABEL } from '../components/admin/PeopleTable'
import { FilterChips, type FilterChip } from '../components/FilterChips'
import { Picker } from '../components/Picker'
import { PersonPanel } from '../components/admin/PersonPanel'
import { BulkGrant } from '../components/admin/BulkGrant'
import { InvitePerson } from '../components/admin/InvitePerson'
import {
  INVITE_OUTCOME_LABEL,
  sendInvitations,
  summariseInvites,
} from '../lib/invites'
import { accessWhy, isAccessDone, setAccess, summariseAccess } from '../lib/access'
import { WarningsFlag, type PageWarning } from '../components/admin/WarningsFlag'
import { SearchIcon } from '../components/icons'
import { loadOrgUnits, type OrgUnitOption } from '../lib/admin'
import { loadBuPlaces, whereLabel, whereOptions, whereOrgUnitIds, whereTree, type BuPlace } from '../lib/where'
import {
  ASSIGNABLE_ROLES,
  GRANT_CATALOGUE,
  countActiveTenantAdmins,
  holdsPrivilegedRole,
  isRegional,
  loadJobTitles,
  loadPeopleScope,
  loadRoster,
  regionWords,
  type AccessStatus,
  type PeopleScope,
  type PeopleSort,
  type PersonRoles,
} from '../lib/roles'
import { RoleSuggestions } from '../components/admin/RoleSuggestions'
import { holderWordsByRole, loadTypicalHolders } from '../lib/roleSuggestions'
import { messageOf } from '../lib/errors'

/**
 * People and roles.
 *
 * ── WHY THIS IS A PAGE AND NOT A CARD ON THE FEED ───────────────────────────
 *
 * Role administration shipped in 021 as a card at the bottom of the home feed.
 * At ~900 people that is wrong: the screen where somebody decides who can
 * address the whole company should not be reached by scrolling past the whole
 * company's posts.
 *
 * ── THE LAYOUT (137, David 9 Oct) ───────────────────────────────────────────
 *
 * The table starts near the top of the page. Above it are two thin rows and
 * nothing else: the count, a notices flag and "Invite someone" (a normal-sized
 * button, top right); then search and six filter pills. Chosen filter values
 * come back as chips that can be ×'d out. What used to sit there full time —
 * a full-width invite bar, the one-administrator notice, a paragraph about how
 * the filters combine — is either a dialog, behind the flag, or gone.
 *
 * ── ONE PERSON AT A TIME, IN A TRAY ─────────────────────────────────────────
 *
 * The table shows what everyone holds; changing anything opens a right-hand
 * tray for one named person, the store items' surface (REC-65). One at a time
 * is not a layout preference: person_admin_detail is a per-person RPC gated on
 * admin.roles precisely so ~900 colleagues' personal addresses never become a
 * column somebody can page through.
 *
 * Bulk is the exception, and it is bounded: one role, to a selection, with the
 * same per-person guards and audit rows. See BulkGrant.
 *
 * ── TWO ROLES REACH THIS PAGE (137) ─────────────────────────────────────────
 *
 * admin.roles is held by tenant_admin and hr_admin. Only tenant_admin holds
 * admin.roles.privileged, without which the Tenant and HR Administrator roles —
 * and anyone who holds one — are read-only here. The database refuses either
 * way (app.require_privileged_for); this page says so before anyone presses.
 */

/**
 * The account states somebody can filter on. Not 042's 'unknown', which is a
 * fact about the CALLER ("you may not see this"), not a state a person is in.
 */
const ACCESS_FILTERS = [
  'active',
  'invited',
  'not_invited',
  'unverified',
  'suspended',
] as const satisfies readonly AccessStatus[]

/** The filter groups, in the order the pills and chips appear. */
type Group = 'Where' | 'Department' | 'Job title' | 'Role' | 'Access'

/**
 * 154. Two sections of one screen (rule 18: a .tab is a section): People, the
 * roster; and Suggestions, who looks like a role's typical holder and does not
 * hold it. A route each (#/admin, #/admin/suggestions), the AdminShop recipe.
 */
export type AdminTab = 'people' | 'suggestions'

export function Admin({ me, tab = 'people' }: { me: Me; tab?: AdminTab }) {
  const [people, setPeople] = useState<PersonRoles[] | null>(null)
  const [total, setTotal] = useState(0)
  const [orgUnits, setOrgUnits] = useState<OrgUnitOption[]>([])
  // 140. Every store, for the store-manager picker in the panel.
  const [stores, setStores] = useState<StoreOption[]>([])
  const [titles, setTitles] = useState<{ title: string; people: number }[]>([])
  // 154. Each confirmed typical holder's words, by role key: the panel prints
  // them beside the role, so a grant by hand reads what a suggestion says.
  const [holderWords, setHolderWords] = useState<Record<string, { name: string; why: string }[]>>({})
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // Empty means "no filter". OR within one, AND across them — in the database.
  // WHERE: country, entity and business unit in ONE tree (David, 9 Oct). The
  // values are `country:<name>` or org unit ids; lib/where.ts turns them into
  // what the roster is sent.
  const [whereFilter, setWhereFilter] = useState<string[]>([])
  const [places, setPlaces] = useState<BuPlace[]>([])
  // 153. Whose list this is: the whole company, or a regional HR
  // Administrator's region. Read once; the roster itself is already bounded by
  // the server, this is what lets the page SAY so and bound its pickers.
  const [scope, setScope] = useState<PeopleScope | null>(null)
  const regional = isRegional(scope)
  // A regional holder's Where picker offers their region, and the departments
  // (which span every entity and narrow a list that is already theirs).
  const treeUnits = useMemo(
    () => (isRegional(scope) ? orgUnits.filter((o) => o.type === 'department' || scope.unitIds.includes(o.orgUnitId)) : orgUnits),
    [orgUnits, scope],
  )
  const tree = useMemo(() => whereTree(treeUnits, places), [treeUnits, places])
  // The ids the roster is sent. A string key for the effect below, so the tree
  // arriving does not refetch an unfiltered roster.
  const whereIds = useMemo(() => whereOrgUnitIds(whereFilter, tree), [whereFilter, tree])
  const whereKey = whereIds.join(',')
  const [deptFilter, setDeptFilter] = useState<string[]>([])
  const [titleFilter, setTitleFilter] = useState<string[]>([])
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<AccessStatus[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  // Applied by the DATABASE (054). Clicking a header refetches rather than
  // reordering the array — the limit is server-side (039).
  const [sort, setSort] = useState<PeopleSort>('name')
  const [sortDesc, setSortDesc] = useState(false)
  // Tenant-wide, from its own query — counting the visible page would count
  // only the administrators the current filter happens to show.
  const [adminCount, setAdminCount] = useState<number | null>(null)
  const [inviting, setInviting] = useState(false)
  const [outcome, setOutcome] = useState<string | null>(null)
  // 138/139: who a bulk act left as they were, and why — folded under the
  // outcome line, each name opening their panel.
  const [misses, setMisses] = useState<Misses | null>(null)
  // 139: the people the last bulk suspension suspended, so the outcome line
  // can put back exactly them.
  const [undoIds, setUndoIds] = useState<string[]>([])
  const clearOutcome = () => {
    setOutcome(null)
    setMisses(null)
    setUndoIds([])
  }

  const canAdmin = can(me, 'admin.roles')
  // 137. The tenant administrator. Without it the privileged roles, and the
  // people who hold them, are read-only on this page.
  const canPrivileged = can(me, 'admin.roles.privileged')
  const canEditProfile = can(me, 'people.update') || can(me, 'admin.org')
  const canCreateOrgUnit = can(me, 'admin.org')
  // 153. invite_person is unscoped-only: a new person has no employment, so no
  // region could ever see them again.
  const canInviteNew = canEverywhere(me, 'admin.roles')


  // ONLY THE NEWEST REQUEST MAY WRITE THE LIST (27 Sep, David: "it filters,
  // then goes back to the full list"). The roster takes ~5 s unfiltered on prod
  // and ~0.5 s with a name typed, so the debounce does not stop requests
  // overlapping — it only stops them STARTING together. Every call takes a
  // ticket; a response whose ticket is not the latest is dropped, error
  // included, because a stale failure is as wrong as a stale success.
  const latestRequest = useRef(0)

  async function refresh() {
    if (!canAdmin) return
    const ticket = ++latestRequest.current
    try {
      const roster = await loadRoster({
        search,
        includeSuspended: true,
        // One OR'd set of org units, each matching itself and everything
        // beneath it — the same cascade p_legal_entities applies to anything
        // it is given. Business units are not sent separately any more.
        legalEntityIds: whereIds,
        departmentIds: deptFilter,
        jobTitles: titleFilter,
        roleKeys: roleFilter,
        statuses: statusFilter,
        sort,
        sortDesc,
      })
      if (ticket !== latestRequest.current) return
      setPeople(roster.people)
      setTotal(roster.total)
      setError(null)
    } catch (e) {
      if (ticket !== latestRequest.current) return
      setError(messageOf(e))
    }
  }

  async function refreshSideData() {
    if (!canAdmin) return
    await Promise.all([
      countActiveTenantAdmins().then(setAdminCount).catch(() => undefined),
      loadJobTitles().then(setTitles).catch(() => undefined),
      loadTypicalHolders(false)
        .then((h) => setHolderWords(holderWordsByRole(h, me.locale)))
        .catch(() => undefined),
    ])
  }

  // The org tree, the job titles and the admin count. People are loaded by the
  // effect below, which also runs on mount — loading them here too ran two
  // identical queries on every arrival (OPS-28's shape).
  //
  // Guarded on canAdmin because 043 made the roster refuse outright, and hooks
  // run before the early return below. The guard is a courtesy; the refusal is
  // the gate.
  useEffect(() => {
    if (!canAdmin) return
    let live = true
    loadPeopleScope()
      .then((sc) => live && setScope(sc))
      .catch(() => undefined)
    loadOrgUnits()
      .then((o) => live && setOrgUnits(o))
      .catch((e: Error) => live && setError(e.message))
    loadBuPlaces()
      .then((p) => live && setPlaces(p))
      .catch((e: Error) => live && setError(e.message))
    // 140. Rooms are readable by every member (064), so this cannot be
    // refused; a failure only empties the picker, it does not break the page.
    loadStockRooms()
      .then((r) => live && setStores(r.map((x) => ({ id: x.id, name: x.name, isActive: x.isActive }))))
      .catch(() => undefined)
    void refreshSideData()
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAdmin])

  // Debounced so typing a name is not twelve round trips. Every filter runs
  // SERVER-SIDE (039).
  useEffect(() => {
    // 154. The Suggestions tab does not need ~900 people loaded behind it.
    if (tab !== 'people') return
    const t = setTimeout(() => {
      void refresh()
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, whereKey, deptFilter, titleFilter, roleFilter, statusFilter, sort, sortDesc, canAdmin, tab])

  // The tray keeps the person it opened on even if a refresh filters them out
  // of the list (suspend someone while filtered to Active, say) — closing it
  // under the administrator's hand would read as the change having failed.
  const lastSelected = useRef<PersonRoles | null>(null)
  const selected = useMemo(() => {
    if (!selectedId) return null
    const found = people?.find((p) => p.personId === selectedId)
    if (found) lastSelected.current = found
    return found ?? (lastSelected.current?.personId === selectedId ? lastSelected.current : null)
  }, [people, selectedId])

  // 139: suspended people are selectable too, so a group can be restored.
  // The bar offers no role grant while any are selected (a role on a
  // suspended membership does nothing, and assign_role refuses it).
  const isSelectable = (p: PersonRoles) => canPrivileged || !holdsPrivilegedRole(p)

  // ── filter options ───────────────────────────────────────────────────────
  const where = whereOptions(tree)
  const departments = orgUnits.filter((o) => o.type === 'department')
  const roleOptions = [
    // 140: every grant, Store manager included (people_roster matches it
    // against the store lists, not role_assignment).
    ...GRANT_CATALOGUE.map((r) => ({ value: r.key, label: r.name })),
    // Nobody assigns it (021 grants it by trigger), but "who holds ONLY the
    // baseline" is how you find the people no decision has been made about.
    { value: 'employee', label: 'Employee (baseline)' },
  ]

  const chips: FilterChip[] = [
    // A BU name carries its Bamboo number and country ("73 (ConeTec - CL -
    // Santiago)"), so each chip is unique on its own label.
    ...whereFilter.map((v) => ({ group: 'Where', value: v, label: whereLabel(v, where) })),
    ...deptFilter.map((id) => ({
      group: 'Department',
      value: id,
      label: orgUnits.find((u) => u.orgUnitId === id)?.name ?? id,
    })),
    ...titleFilter.map((t) => ({ group: 'Job title', value: t, label: t })),
    ...roleFilter.map((key) => ({
      group: 'Role',
      value: key,
      label: roleOptions.find((r) => r.value === key)?.label ?? key,
    })),
    ...statusFilter.map((s) => ({ group: 'Access', value: s, label: STATE_LABEL[s] })),
  ]

  const setters: Record<Group, (fn: (v: string[]) => string[]) => void> = {
    Where: setWhereFilter,
    Department: setDeptFilter,
    'Job title': setTitleFilter,
    Role: setRoleFilter,
    Access: (fn) => setStatusFilter((v) => fn(v) as AccessStatus[]),
  }

  function clearFilters() {
    setWhereFilter([])
    setDeptFilter([])
    setTitleFilter([])
    setRoleFilter([])
    setStatusFilter([])
    setSearch('')
  }

  // ── notices, behind the flag ─────────────────────────────────────────────
  const warnings: PageWarning[] = []
  if (adminCount !== null && adminCount <= 1 && !regional) {
    // One active tenant administrator is a deliberate decision (ACC-6), so this
    // states the consequence rather than alarming about it.
    warnings.push({
      id: 'one-admin',
      tone: 'warn',
      text: (
        <>
          There is <strong>one</strong> active Tenant Administrator. Changes to
          Tenant and HR Administrators stop while they are away — everything
          else on this page, publishing and the feed are unaffected.
        </>
      ),
    })
  }
  if (isRegional(scope)) {
    warnings.push({
      id: 'region',
      tone: 'info',
      text: (
        <>
          You are the HR Administrator for <strong>{regionWords(scope)}</strong>.
          This list is the people whose legal entity or business unit is in your
          region — nobody else is shown. You can give them any role except
          Tenant and HR Administrator, and Comms Administrator only for a part
          of your region. Inviting someone new, moving a person out of the
          region and anything company-wide are for a group HR Administrator.
        </>
      ),
    })
  }
  if (!canPrivileged) {
    warnings.push({
      id: 'hr-scope',
      tone: 'info',
      text: (
        <>
          You are an HR Administrator. Tenant and HR Administrators — you
          included — have their roles and access changed by a Tenant
          Administrator. You can still edit their profiles.
        </>
      ),
    })
  }
  if (people && total > people.length) {
    warnings.push({
      id: 'page',
      tone: 'info',
      text: (
        <>
          Showing the first {people.length} of {total}. Search or filter to
          reach the rest; “select all” selects the {people.length} shown.
        </>
      ),
    })
  }

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="People and roles"
      canAdmin={canAdmin}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  // Presentation only. Every function this page calls checks admin.roles
  // itself (043 closed the one that did not — ACC-16). If a function is ever
  // added to this page, the check belongs in the function, not here.
  if (!canAdmin) {
    return shell(
      <Card title="Administration">
        <p className="empty">
          This section needs the Tenant Administrator or HR Administrator role.
          Everything you can see about your own account is on your profile.
        </p>
      </Card>,
    )
  }

  // 154. Underlined, not filled: a section of one screen, never a filter (rule 18).
  const tabs = (
    <nav className="tabs people__tabs" aria-label="People and roles sections">
      <Link to="/admin/people" className={`tab${tab === 'people' ? ' tab--on' : ''}`} aria-current={tab === 'people' ? 'page' : undefined}>
        People
      </Link>
      <Link
        to="/admin/suggestions"
        className={`tab${tab === 'suggestions' ? ' tab--on' : ''}`}
        aria-current={tab === 'suggestions' ? 'page' : undefined}
      >
        Suggestions
      </Link>
    </nav>
  )

  if (tab === 'suggestions') {
    return shell(
      <div className="people">
        {tabs}
        <RoleSuggestions
          orgUnits={orgUnits}
          places={places}
          tree={tree}
          scope={scope}
          titles={titles}
          canPrivileged={canPrivileged}
          onGranted={() => void refreshSideData()}
        />
      </div>,
    )
  }

  const filtered = chips.length > 0 || search.trim() !== ''
  const visibleSelectable = (people ?? []).filter(isSelectable)
  // 139. The suspended people in the selection, for "Restore access (n)".
  const selectedSuspended = visibleSelectable.filter((p) => checked.has(p.personId) && p.accessStatus === 'suspended')

  return shell(
    <div className="people">
      {tabs}
      {/* ── ROW 1: the count, the notices, the one action ─────────────────── */}
      <div className="people__bar">
        <p className="people__count" aria-live="polite">
          {people === null ? (
            'Loading people…'
          ) : (
            <>
              <strong>{total}</strong> {total === 1 ? 'person' : 'people'}
              {filtered && <span className="people__countnote"> match</span>}
              {total > people.length && (
                <span className="people__countnote"> · first {people.length} shown</span>
              )}
              {isRegional(scope) && (
                <span className="people__countnote"> · in {regionWords(scope)}</span>
              )}
            </>
          )}
        </p>
        <div className="people__actions">
          <WarningsFlag warnings={warnings} />
          {canInviteNew && (
            <button type="button" className="btn btn--primary btn--sm" onClick={() => setInviting(true)}>
              Invite someone
            </button>
          )}
        </div>
      </div>

      {outcome && (
        <div className="people__outcomewrap" role="status">
          <p className="people__outcome">
            {outcome}
            <button
              type="button"
              className="chip__x"
              aria-label="Dismiss"
              onClick={clearOutcome}
            >
              ×
            </button>
          </p>
          {undoIds.length > 0 && (
            <button
              type="button"
              className="btn btn--sm people__undo"
              onClick={async () => {
                try {
                  const results = await setAccess(undoIds, true)
                  clearOutcome()
                  setOutcome(summariseAccess(results, true))
                  await refresh()
                } catch (e) {
                  setError(messageOf(e))
                }
              }}
            >
              Undo — restore these {undoIds.length}
            </button>
          )}
          {misses && misses.items.length > 0 && (
            <details className="people__misses">
              <summary>{misses.title}</summary>
              <ul>
                {misses.items.map((m) => (
                  <li key={m.personId}>
                    {m.displayName ? (
                      <button type="button" className="btn btn--link" onClick={() => setSelectedId(m.personId)}>
                        {m.displayName}
                      </button>
                    ) : (
                      'Someone'
                    )}{' '}
                    — {m.why}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {/* ── ROW 2: search and the filters ──────────────────────────────────
          Pickers, not chip rows: a chip row shows every option at once, and
          there are 42 business units and 267 job titles. The CHOICES come back
          as chips underneath, so the picker is never the only place the
          current filter is written down (046). */}
      <div className="people__filters" role="search" aria-label="Find people">
        <label className="people__search">
          <span className="searchwrap__icon" aria-hidden="true">
            <SearchIcon size={15} />
          </span>
          <span className="visually-hidden">Find someone by name</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find someone"
            autoComplete="off"
          />
        </label>

        <Picker
          variant="pill"
          className="picker--where"
          label="Where"
          searchPlaceholder="Country, entity or business unit"
          // Country → legal entity → business unit, each level sorted by name
          // (BUs in their Bamboo-number order). Choosing a level includes
          // everything under it, and the rows beneath show a muted tick.
          options={where}
          selected={whereFilter}
          onChange={setWhereFilter}
        />
        <Picker
          variant="pill"
          label="Department"
          searchPlaceholder="Department"
          options={departments.map((o) => ({ value: o.orgUnitId, label: o.name }))}
          selected={deptFilter}
          onChange={setDeptFilter}
        />
        <Picker
          variant="pill"
          label="Job title"
          searchPlaceholder="Job title"
          options={titles.map((t) => ({ value: t.title, label: t.title, count: t.people }))}
          selected={titleFilter}
          onChange={setTitleFilter}
        />
        <Picker
          variant="pill"
          label="Role"
          searchPlaceholder="Role"
          options={roleOptions}
          selected={roleFilter}
          onChange={setRoleFilter}
        />
        <Picker
          variant="pill"
          label="Access"
          searchPlaceholder="Account state"
          // The same strings the Access cell renders, from the same column. A
          // state here the table cannot show, or the reverse, is how a filter
          // starts lying.
          options={ACCESS_FILTERS.map((s) => ({ value: s, label: STATE_LABEL[s] }))}
          selected={statusFilter}
          onChange={(next) => setStatusFilter(next as AccessStatus[])}
        />
      </div>

      <FilterChips
        chips={chips}
        onRemove={(c) => setters[c.group as Group]((v) => v.filter((x) => x !== c.value))}
        onClear={clearFilters}
      />


      <div className="people__tablecard">
        {people === null && !error && (
          <p className="empty people__empty" aria-busy="true">
            Loading people…
          </p>
        )}

        {people && people.length === 0 && (
          <div className="people__empty">
            <p className="empty">
              {filtered
                ? 'Nobody matches those filters.'
                : isRegional(scope)
                  ? `Nobody’s legal entity or business unit is in your region (${regionWords(scope)}) yet.`
                  : 'Nobody has a tenant membership yet. Roles follow membership, so this fills up when the HR roster loads.'}
            </p>
            {filtered && (
              <button type="button" className="btn btn--quiet btn--sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        )}

        {people && people.length > 0 && (
          <PeopleTable
            people={people}
            myPersonId={me.personId}
            selectedId={selectedId}
            checked={checked}
            sort={sort}
            sortDesc={sortDesc}
            // Clicking the column you are on flips the direction; a new column
            // starts ascending. Carrying the direction over gives Z–A for a
            // column you have never sorted, which reads as broken.
            onSort={(c) => {
              if (c === sort) setSortDesc((d) => !d)
              else {
                setSort(c)
                setSortDesc(false)
              }
            }}
            onSelect={setSelectedId}
            isSelectable={isSelectable}
            onToggleCheck={(id) => {
              setChecked((prev) => {
                const next = new Set(prev)
                if (next.has(id)) next.delete(id)
                else next.add(id)
                return next
              })
            }}
            // Select every selectable person SHOWN, or clear. Never the 931:
            // a selection of people the administrator cannot see is a bulk
            // grant to strangers.
            onToggleAll={() => {
              setChecked((prev) =>
                visibleSelectable.some((p) => prev.has(p.personId))
                  ? new Set()
                  : new Set(visibleSelectable.map((p) => p.personId)),
              )
            }}
          />
        )}
      </div>

      {/* Under the table and sticky to the bottom edge, so it is in view
          wherever the selection was made. */}
      {checked.size > 0 && (
        <BulkGrant
          personIds={[...checked]}
          orgUnits={orgUnits}
          roles={ASSIGNABLE_ROLES.filter((r) => canPrivileged || !r.privileged)}
          scope={scope}
          // Filter Access to "No invitation", select all, Send invitations:
          // that is the onboarding run in three presses.
          onInvite={async () => {
            const results = await sendInvitations([...checked])
            clearOutcome()
            setOutcome(summariseInvites(results))
            setMisses({
              title: 'Who was not invited, and why',
              items: results
                .filter((r) => r.outcome !== 'queued' && r.outcome !== 'already_queued_in_this_wave')
                .map((r) => ({ personId: r.personId, displayName: r.displayName, why: INVITE_OUTCOME_LABEL[r.outcome] })),
            })
            setChecked(new Set())
            await refresh()
          }}
          // 139. Revoke access for a group. The server skips you and anyone who
          // administers people, and the outcome line offers Undo.
          suspendedCount={selectedSuspended.length}
          onSuspend={async (reason) => {
            const results = await setAccess([...checked], false, reason)
            clearOutcome()
            setOutcome(summariseAccess(results, false))
            setUndoIds(results.filter((r) => r.outcome === 'suspended').map((r) => r.personId))
            setMisses({
              title: 'Who was not suspended, and why',
              items: results
                .filter((r) => !isAccessDone(r.outcome) && r.outcome !== 'already_suspended')
                .map((r) => ({
                  personId: r.personId,
                  displayName: r.displayName,
                  why: accessWhy(r),
                })),
            })
            setChecked(new Set())
            await refresh()
          }}
          onRestore={async () => {
            const results = await setAccess(selectedSuspended.map((p) => p.personId), true)
            clearOutcome()
            setOutcome(summariseAccess(results, true))
            setMisses({
              title: 'Who was not restored, and why',
              items: results
                .filter((r) => !isAccessDone(r.outcome) && r.outcome !== 'already_active')
                .map((r) => ({
                  personId: r.personId,
                  displayName: r.displayName,
                  why: accessWhy(r),
                })),
            })
            setChecked(new Set())
            await refresh()
          }}
          onDone={async () => {
            setChecked(new Set())
            await refresh()
          }}
          onCancel={() => setChecked(new Set())}
        />
      )}

      {selected && (
        <Dialog
          open
          title={selected.displayName}
          variant="tray"
          className="people__tray"
          onClose={() => setSelectedId(null)}
        >
          <PersonPanel
            person={selected}
            orgUnits={orgUnits}
            colleagues={(people ?? []).map((c) => ({
              personId: c.personId,
              displayName: c.displayName,
            }))}
            myPersonId={me.personId}
            activeAdminCount={adminCount ?? 1}
            canPrivileged={canPrivileged}
            canEditProfile={canEditProfile}
            canCreateOrgUnit={canCreateOrgUnit}
            canShopAdmin={can(me, 'shop.admin')}
            canViewAs={can(me, 'people.view_as')}
            stores={stores}
            scope={scope}
            typicalHolders={holderWords}
            places={places}
            onChanged={async () => {
              await refresh()
              await refreshSideData()
            }}
          />
        </Dialog>
      )}

      <Dialog open={inviting} title="Invite someone" onClose={() => setInviting(false)}>
        <InvitePerson
          orgUnits={orgUnits}
          onInvited={async (message) => {
            setInviting(false)
            clearOutcome()
            setOutcome(message)
            await refresh()
          }}
          onCancel={() => setInviting(false)}
        />
      </Dialog>
    </div>,
  )
}

type Misses = { title: string; items: { personId: string; displayName: string | null; why: string }[] }

/** Same rule as the hub's avatar: first letters of the first two words. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}
